import { ReactNode } from 'react';
import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { FormSingleSelect } from '@/components/forms/form-single-select.js';
import { FormTextArea } from '@/components/forms/form-text-area.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { Label } from '@/components/ui/label.js';
import { getCoachesGoogleMeetTqReview, updateCoachGoogleMeetTqReview } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import {
  CoachGoogleMeetTqReview,
  GoogleMeetEvaluationStatus,
  GoogleMeetEvaluationStatusLabelMap,
  googleMeetEvaluationStatusSchema,
} from '@airreading/common/data';
import { GoogleMeetEvaluationStatusColorMap } from './google-meet-evaluation-status.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';

// Radix Select can't use an empty/null value, so we represent "no status" with a
// sentinel option and map it back to null when submitting.
const NO_STATUS_VALUE = 'none';

const editFormSchema = z.object({
  googleMeetNotes: z.string().optional(),
  googleMeetEvaluationStatus: z.union([googleMeetEvaluationStatusSchema, z.literal(NO_STATUS_VALUE)]),
});

type EditForm = z.infer<typeof editFormSchema>;

type EvaluationStatusOption = GoogleMeetEvaluationStatus | typeof NO_STATUS_VALUE;

const renderEvaluationStatus = (value: EvaluationStatusOption) =>
  value === NO_STATUS_VALUE ? (
    '—'
  ) : (
    <span className={GoogleMeetEvaluationStatusColorMap[value]}>{GoogleMeetEvaluationStatusLabelMap[value]}</span>
  );

export interface EditGoogleMeetTqReviewProps {
  coach: CoachGoogleMeetTqReview;
  trigger: ReactNode;
}

export const EditGoogleMeetTqReview = ({ coach, trigger }: EditGoogleMeetTqReviewProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({ mutationFn: updateCoachGoogleMeetTqReview });

  const onSubmit = async ({ formData, closeModal }: SubmitProps<EditForm>) => {
    try {
      await mutateAsync({
        coachId: coach.id,
        data: {
          googleMeetNotes: formData.googleMeetNotes ?? null,
          googleMeetEvaluationStatus:
            formData.googleMeetEvaluationStatus === NO_STATUS_VALUE ? null : formData.googleMeetEvaluationStatus,
        },
      });

      await queryClient.invalidateQueries({ queryKey: getCoachesGoogleMeetTqReview().queryKey });

      toast({ description: 'Google Meet TQ review updated successfully' });
      closeModal();
    } catch (e: unknown) {
      toast({
        description:
          airServerErrorFrom(e)?.userFacingMessage ?? 'Failed to update Google Meet TQ review. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const defaultValues: DefaultValues<EditForm> = {
    googleMeetNotes: coach.googleMeetNotes ?? undefined,
    googleMeetEvaluationStatus: coach.googleMeetEvaluationStatus ?? NO_STATUS_VALUE,
  };

  return (
    <FormModal
      modalTitle="Edit"
      formSchema={editFormSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      defaultValues={defaultValues}
      confirmBtnLabel="Save"
      shouldResetOnCancel
    >
      {({ form }) => (
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="font-bold">Teacher name</Label>
            <div className="text-sm text-muted-foreground">{coach.fullNameOrEmail ?? coach.email}</div>
            {/* Matches the reserved FormMessage height in the fields below so all groups are evenly spaced */}
            <div className="h-4" aria-hidden />
          </div>
          <FormSingleSelect
            name="googleMeetEvaluationStatus"
            label="Google Meet evaluation status"
            form={form}
            options={[...googleMeetEvaluationStatusSchema.options, NO_STATUS_VALUE]}
            renderOption={renderEvaluationStatus}
            renderOptionSelected={renderEvaluationStatus}
            triggerProps={{ className: 'w-full' }}
          />
          <FormTextArea name="googleMeetNotes" label="Google Meet notes" form={form} />
        </div>
      )}
    </FormModal>
  );
};
